import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

export function FlutterHeartCloud({ user, profile, onNavigate }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [restartTrigger, setRestartTrigger] = useState(0);
  const settingsRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const gameRef = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!gameOver) return;
    setScoreSaved(false);
    supabase.from('cloud_leaderboard').insert([{
      user_id: user.id,
      score: gameRef.current.score,
      username: profile?.username || null,
      email: user.email,
    }]).then(({ error }) => {
      if (error) console.error('Error saving score:', error);
      setScoreSaved(true);
    });
  }, [gameOver, user, profile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    gameRef.current = {
      started: false,
      over: false,
      score: 0,
      bird: { y: 300, velocity: 0 },
      clouds: [],
      cloudTimer: 0,
      nextSpawn: 40,
      animationId: null,
      flapAngle: 0,
      deathAnim: null,
      tickCount: 0,
    };

    const ctx = canvas.getContext('2d');
    const game = gameRef.current;

    const GRAVITY = 0.5;
    const FLAP = -9;
    const BIRD_X = 60;
    const BIRD_SIZE = 20;
    const BASE_CLOUD_SPEED = 3.2;

    // Spawn a single floating cloud at a random height
    const spawnCloud = () => {
      const baseR = 22 + Math.random() * 16;
      const puffs = [
        { dx: 0,             dy: 0,             r: baseR },
        { dx: baseR * 0.88,  dy: -baseR * 0.08, r: baseR * 0.82 },
        { dx: -baseR * 0.82, dy: -baseR * 0.05, r: baseR * 0.78 },
        { dx: baseR * 1.6,   dy: baseR * 0.18,  r: baseR * 0.65 },
        { dx: -baseR * 1.55, dy: baseR * 0.12,  r: baseR * 0.62 },
        { dx: baseR * 0.42,  dy: -baseR * 0.58, r: baseR * 0.68 },
        { dx: -baseR * 0.32, dy: -baseR * 0.52, r: baseR * 0.64 },
      ];
      const margin = 70;
      const cy = margin + Math.random() * (canvas.height - margin * 2);
      const leftmost = Math.min(...puffs.map(p => p.dx - p.r));
      const rightmost = Math.max(...puffs.map(p => p.dx + p.r));
      // cx starts so the cloud's left edge is just off the right side of the canvas
      const cx = canvas.width - leftmost;
      game.clouds.push({ cx, cy, puffs, leftmost, rightmost, scored: false });
    };

    const drawCloud = (cloud) => {
      ctx.fillStyle = '#fff';
      ctx.shadowColor = 'rgba(140,120,200,0.28)';
      ctx.shadowBlur = 16;
      cloud.puffs.forEach(p => {
        ctx.beginPath();
        ctx.arc(cloud.cx + p.dx, cloud.cy + p.dy, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.shadowBlur = 0;
    };

    // Heart drawing helpers
    const drawHeartPath = (size) => {
      ctx.moveTo(0, size * 0.4);
      ctx.bezierCurveTo(-size * 0.1, size * 0.2, -size * 0.65, -size * 0.1, -size * 0.65, -size * 0.15);
      ctx.bezierCurveTo(-size * 0.65, -size * 0.55, -size * 0.1, -size * 0.65, 0, -size * 0.35);
      ctx.bezierCurveTo(size * 0.1, -size * 0.65, size * 0.65, -size * 0.55, size * 0.65, -size * 0.15);
      ctx.bezierCurveTo(size * 0.65, -size * 0.1, size * 0.1, size * 0.2, 0, size * 0.4);
      ctx.closePath();
    };

    const getZigZag = (size) => [
      { x: size * 0.05,  y: -size * 0.65 },
      { x: -size * 0.12, y: -size * 0.35 },
      { x: size * 0.1,   y: -size * 0.1  },
      { x: -size * 0.08, y:  size * 0.15 },
      { x: size * 0.06,  y:  size * 0.4  },
    ];

    const render = () => {
      // Sky gradient — slightly different blue-ish tone from Flutter Heart
      const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grad.addColorStop(0, '#e0f0ff');
      grad.addColorStop(1, '#f0e8ff');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Floating clouds
      game.clouds.forEach(cloud => drawCloud(cloud));

      // Heart character
      const hx = BIRD_X + BIRD_SIZE / 2;
      const hy = game.bird.y + BIRD_SIZE / 2;
      const s = BIRD_SIZE * 0.62;
      const tilt = Math.min(Math.max(game.bird.velocity * 0.035, -0.4), 0.7);
      game.flapAngle += 0.14;
      const flap = Math.sin(game.flapAngle) * s * 0.45;

      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(tilt);

      const drawWing = (side) => {
        const d = side;
        const wGrad = ctx.createLinearGradient(d * s * 0.3, 0, d * s * 2.2, 0);
        wGrad.addColorStop(0, 'rgba(255,220,240,1)');
        wGrad.addColorStop(0.5, 'rgba(255,240,250,0.97)');
        wGrad.addColorStop(1, 'rgba(255,255,255,0.85)');
        ctx.fillStyle = wGrad;
        ctx.beginPath();
        ctx.moveTo(d * s * 0.28, s * 0.05);
        ctx.bezierCurveTo(d * s * 0.9, -s * 0.35 + flap, d * s * 2.05, -s * 0.15 + flap, d * s * 2.2, s * 0.28 + flap * 0.3);
        ctx.bezierCurveTo(d * s * 1.7, s * 0.55, d * s * 0.75, s * 0.38, d * s * 0.28, s * 0.28);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(230,170,210,0.55)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.strokeStyle = 'rgba(210,150,200,0.35)';
        ctx.lineWidth = 0.9;
        [[d*s*2.0, -s*0.1+flap*0.9], [d*s*1.5, -s*0.2+flap*0.7],
         [d*s*1.0, -s*0.25+flap*0.5], [d*s*0.6, -s*0.18+flap*0.3]].forEach(([fx, fy]) => {
          ctx.beginPath();
          ctx.moveTo(d * s * 0.28, s * 0.18);
          ctx.quadraticCurveTo(d * s * 0.5, fy * 0.5, fx, fy);
          ctx.stroke();
        });
      };

      if (!game.deathAnim) {
        drawWing(-1);
        drawWing(1);
      }

      // Heart body
      const heartSize = BIRD_SIZE * 1.3;
      if (!game.deathAnim) {
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
      } else {
        const t = game.deathAnim.frame / 40;
        const spread = t * s * 2.5;
        const drop = t * t * s * 3;
        const spin = t * 1.0;
        const alpha = Math.max(0, 1 - t);
        const zz = getZigZag(heartSize);
        ctx.globalAlpha = alpha;

        const leftClip = () => {
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(-heartSize * 2, zz[zz.length - 1].y);
          ctx.lineTo(-heartSize * 2, -heartSize * 2);
          ctx.lineTo(zz[0].x, -heartSize * 2);
          ctx.closePath();
          ctx.clip();
        };

        const rightClip = () => {
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(heartSize * 2, zz[zz.length - 1].y);
          ctx.lineTo(heartSize * 2, -heartSize * 2);
          ctx.lineTo(zz[0].x, -heartSize * 2);
          ctx.closePath();
          ctx.clip();
        };

        ctx.save();
        ctx.translate(-spread, drop);
        ctx.rotate(-spin);
        leftClip();
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.translate(spread, drop);
        ctx.rotate(spin);
        rightClip();
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
        ctx.restore();

        ctx.globalAlpha = 1;
      }

      ctx.restore();

      // Shockwave on death
      if (game.deathAnim && game.deathAnim.frame < 20) {
        const t = game.deathAnim.frame / 20;
        ctx.save();
        ctx.translate(game.deathAnim.x, game.deathAnim.y);
        ctx.strokeStyle = `rgba(244,63,94,${1 - t})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, t * BIRD_SIZE * 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // Score
      ctx.fillStyle = 'rgba(100,120,180,0.8)';
      ctx.font = 'bold 22px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(game.score, canvas.width / 2, 36);
      ctx.textAlign = 'left';

      // Start message
      if (!game.started) {
        ctx.fillStyle = 'rgba(240,248,255,0.7)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#4060a0';
        ctx.font = '600 16px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Click or press Space to start', canvas.width / 2, canvas.height / 2);
        ctx.textAlign = 'left';
      }
    };

    const update = () => {
      if (!game.started || game.over) return;

      game.tickCount++;
      game.bird.velocity += GRAVITY;
      game.bird.y += game.bird.velocity;

      // Hit top or bottom
      if (game.bird.y < 0 || game.bird.y + BIRD_SIZE > canvas.height) {
        game.over = true;
        setGameOver(true);
        return;
      }

      const heartCX = BIRD_X + BIRD_SIZE / 2;
      const heartCY = game.bird.y + BIRD_SIZE / 2;
      const heartR = BIRD_SIZE * 0.42;

      // Move clouds, check per-puff collision, score
      game.clouds.forEach(cloud => {
        const CLOUD_SPEED = BASE_CLOUD_SPEED + game.score * 0.08;
        cloud.cx -= CLOUD_SPEED;

        if (!game.over) {
          for (const puff of cloud.puffs) {
            const px = cloud.cx + puff.dx;
            const py = cloud.cy + puff.dy;
            const dx = heartCX - px;
            const dy = heartCY - py;
            if (dx * dx + dy * dy < (heartR + puff.r * 0.82) ** 2) {
              game.over = true;
              game.deathAnim = { frame: 0, x: heartCX, y: heartCY };
              break;
            }
          }
        }

        // Score when cloud's right edge passes the bird
        if (!cloud.scored && cloud.cx + cloud.rightmost < BIRD_X) {
          cloud.scored = true;
          game.score++;
          setScore(game.score);
        }
      });

      // Remove off-screen clouds
      game.clouds = game.clouds.filter(c => c.cx + c.rightmost > -10);

      // Spawn clouds at randomised intervals
      game.cloudTimer++;
      if (game.cloudTimer > game.nextSpawn) {
        spawnCloud();
        // occasionally spawn a second cloud right behind the first
        if (Math.random() < 0.4) spawnCloud();
        game.cloudTimer = 0;
        game.nextSpawn = 35 + Math.floor(Math.random() * 25);
      }
    };

    const DEATH_FRAMES = 40;

    const gameLoop = () => {
      update();
      render();
      if (!game.over) {
        game.animationId = requestAnimationFrame(gameLoop);
      } else if (game.deathAnim && game.deathAnim.frame <= DEATH_FRAMES) {
        game.deathAnim.frame++;
        render();
        if (game.deathAnim.frame <= DEATH_FRAMES) {
          game.animationId = requestAnimationFrame(gameLoop);
        } else {
          setGameOver(true);
        }
      }
    };

    const flap = () => {
      if (!game.started) { game.started = true; }
      else if (!game.over) { game.bird.velocity = FLAP; }
    };

    const handleKeyDown = (e) => { if (e.code === 'Space') { e.preventDefault(); flap(); } };
    const handleClick = () => flap();
    const handleTouch = (e) => { e.preventDefault(); flap(); };

    render();
    gameLoop();
    window.addEventListener('keydown', handleKeyDown);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouch, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouch);
      cancelAnimationFrame(game.animationId);
    };
  }, [restartTrigger]);

  const handlePlayAgain = () => {
    setScore(0);
    setGameOver(false);
    setRestartTrigger(t => t + 1);
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <div className="settings-menu" ref={settingsRef}>
          <button className="settings-btn" onClick={() => setSettingsOpen(v => !v)} aria-label="Settings">⚙️</button>
          {settingsOpen && (
            <div className="settings-dropdown">
              <div className="settings-email">{user.email}</div>
              <hr className="settings-divider" />
              <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('edit-profile'); }}>Edit Username</button>
              {profile?.is_admin && (
                <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('admin'); }}>Admin Panel</button>
              )}
              <hr className="settings-divider" />
              <button className="settings-item settings-item-danger" onClick={() => supabase.auth.signOut()}>Logout</button>
            </div>
          )}
        </div>
        <span className="game-username">{profile?.username || user.email}</span>
        <button className="btn back-btn" onClick={() => navigate('/')}>← Games</button>
      </div>

      <h1 className="game-title">☁️ Cloud Dodge</h1>
      <canvas ref={canvasRef} width={500} height={600} className="game-canvas" />

      <button className="btn leaderboard-btn" onClick={() => onNavigate('leaderboard')}>
        🏆 Leaderboard
      </button>
      <button className="btn leaderboard-btn" onClick={() => onNavigate('leagues')}>
        🏅 Leagues
      </button>

      {gameOver && (
        <div className="game-over-modal">
          <div className="game-over-box">
            <h2>Game Over!</h2>
            <p className="final-score">{score}</p>
            <button onClick={handlePlayAgain} className="btn">Play Again</button>
            <button onClick={() => onNavigate('leaderboard')} className="btn btn-submit" disabled={!scoreSaved}>
              {scoreSaved ? 'View Leaderboard' : 'Saving…'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}